import * as THREE from 'three/webgpu';
import { MeshLine, MeshLineGeometry } from 'makio-meshline';
import {
  Fn, float, smoothstep,
  uniform as tslUniform,
  cameraPosition, positionWorld, distance,
} from 'three/tsl';
import type { UniformNode } from 'three/webgpu';
import { QualityMode } from '@/utils/quality';

const MAX_POINTS = 4000;

function resampleByArcLength(points: THREE.Vector3[], spacing: number) {
  if (points.length < 2) return points.slice();

  const out = [points[0].clone()];
  let traveled = 0;
  let nextTarget = spacing;

  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const segLen = a.distanceTo(b);
    if (segLen === 0) continue;

    while (traveled + segLen >= nextTarget) {
      const t = (nextTarget - traveled) / segLen;
      out.push(a.clone().lerp(b, t));
      nextTarget += spacing;
    }
    traveled += segLen;
  }

  const last = points[points.length - 1];
  if (out[out.length - 1].distanceToSquared(last) > 1e-6) {
    out.push(last.clone());
  }
  return out;
}

export type BallTrailOptions = {
  maxPoints?: number;
  lineWidth?: number;
  fadeLength?: number;
  resampleSpacing?: number;
  cameraFadeNear?: number;
  cameraFadeFar?: number;
  color?: THREE.Color | number;
  qualityLevel?: QualityMode;
  /** Render layer. Layers 1/2 are per-eye in XR — MR passes 0. */
  layer?: number;
  /**
   * Sample the ball position every Nth frame. Defaults to the qualityLevel
   * rate (Low 10, otherwise 4). Short shots need a denser rate: a ~1 s chip at
   * Low yields only ~7 raw samples, and resampleByArcLength only lerps between
   * them, so the frozen arc reads as chords rather than a curve.
   */
  sampleEvery?: number;
  /**
   * Where the near-camera fade measures distance from.
   *
   * 'camera' uses the TSL `cameraPosition` built-in. **That path is unusable in
   * XR on three r185**: its ArrayCamera branch attaches `onRenderUpdate` to a
   * `uniformArray`, which shadows `UniformArrayNode.prototype.update`, and
   * `setup()` then calls it with no frame — throwing at shader-build time.
   * NodeManager silently swaps in a bare NodeMaterial, and since MeshLine's
   * whole ribbon expansion lives in its `vertexNode`, every triangle collapses
   * to zero area and the trail vanishes with no visible error.
   *
   * 'uniform' reads a viewer position the app feeds via setViewerPosition().
   * Also the better choice in stereo regardless: `cameraPosition` is per-eye,
   * so the fade edge would land differently in each eye.
   */
  viewerFade?: 'camera' | 'uniform';
};

export class BallTrail {
  scene: THREE.Object3D;
  golfBall: THREE.Object3D;
  maxPoints: number;
  lineWidth: number;
  fadeLength: number;
  resampleSpacing: number;
  color: THREE.Color;
  qualityLevel: QualityMode;
  layer: number;
  sampleEvery: number;

  points: THREE.Vector3[];
  frameNum: number;
  line: MeshLine;
  fadeFracUniform: UniformNode<"float", number>;
  camFadeNear: UniformNode<"float", number>;
  camFadeFar: UniformNode<"float", number>;
  activeRatioUniform: UniformNode<"float", number>;
  /** Viewer position for the near fade when viewerFade is 'uniform'. */
  readonly uViewer = tslUniform(new THREE.Vector3());

  renderOrder = 1;
  #positions: Float32Array;
  #activePoints = 0;
  #built = false;

  constructor(scene: THREE.Object3D, golfBall: THREE.Object3D, options: BallTrailOptions = {}) {
    this.scene = scene;
    this.golfBall = golfBall;
    this.layer = options.layer ?? 2;
    this.maxPoints = options.maxPoints ?? MAX_POINTS;
    this.lineWidth = options.lineWidth ?? 0.03;
    this.fadeLength = options.fadeLength ?? 2.0;
    this.resampleSpacing = options.resampleSpacing ?? 0.15;
    this.qualityLevel = options.qualityLevel ?? QualityMode.Medium;
    this.sampleEvery = Math.max(
      1,
      Math.floor(options.sampleEvery ?? (this.qualityLevel === QualityMode.Low ? 10 : 4))
    );
    this.color = options.color instanceof THREE.Color
      ? options.color
      : new THREE.Color(options.color ?? '#fc4723');

    this.points = [];
    this.frameNum = 0;

    // Uniforms for dynamic fade control
    this.fadeFracUniform = tslUniform(0.01);
    this.camFadeNear = tslUniform(options.cameraFadeNear ?? 15);
    this.camFadeFar = tslUniform(options.cameraFadeFar ?? 20);
    this.activeRatioUniform = tslUniform(1.0);

    // TSL hook: fade opacity at both ends of the trail.
    // Receives (alpha, progress, side) where progress is 0→1 along the line.
    // Returns modified alpha with smoothstep fade at both ends.
    // @ts-expect-error - makio-meshline Fn hook typing
    const trailOpacityFn = Fn(([alpha, progress, side]) => {
      // const fadeIn = smoothstep(float(0), this.fadeFracUniform, progress);
      // const fadeOut = smoothstep(float(0), this.fadeFracUniform, float(1).sub(progress));
      // Remap progress from [0, activeRatio] to [0, 1]
      const remapped = progress.div(this.activeRatioUniform).clamp(0, 1);
      const fadeIn = smoothstep(float(0), this.fadeFracUniform, remapped);
      const fadeOut = smoothstep(float(0), this.fadeFracUniform, float(1).sub(remapped));
      return alpha.mul(fadeIn).mul(fadeOut);
    });

    // TSL hook: fade based on viewer distance.
    // Receives (alpha, uv, progress, side). Fully transparent when close
    // to the viewer (< cameraFadeNear), fully opaque past cameraFadeFar.
    // Resolved once here, at graph-build time — see BallTrailOptions.viewerFade
    // for why XR must never reach the cameraPosition built-in.
    const viewer: any = options.viewerFade === 'uniform' ? this.uViewer : cameraPosition;
    // @ts-expect-error - makio-meshline Fn hook typing
    const trailAlphaFn = Fn(([alpha, uv, progress, side]) => {
      const dist = distance(positionWorld, viewer);
      const camFade = smoothstep(this.camFadeNear, this.camFadeFar, dist);
      return alpha.mul(camFade);
    });

    // Pre-allocate fixed-size positions buffer
    this.#positions = new Float32Array(this.maxPoints * 3);

    // Create the line with dummy points — updated in _rebuild
    this.line = new MeshLine({
      // lines: new Float32Array([0, 0, 0, 0.001, 0, 0]),
      lines: this.#positions,
      color: this.color,
      lineWidth: this.lineWidth,
      sizeAttenuation: true,
      transparent: true,
      opacity: 1.0,
      opacityFn: trailOpacityFn,
      fragmentAlphaFn: trailAlphaFn,
    });

    // Build immediately to pre-allocate GPU buffers and compile shaders
    this.line.lines(this.#positions).build();
    this.#built = true;

    this.line.layers.set(this.layer);
    this.line.frustumCulled = false;
    this.line.visible = false;
    this.line.renderOrder = this.renderOrder;
    // Never clipped by the MR world mask: the tracer's whole job is to be
    // readable once the ball has left the window. Set here so no page can miss
    // it, and so it survives reset().
    this.line.userData.mrMaskExempt = true;
    // @ts-expect-error makio-meshline raycast signature doesn't match Object3D
    scene.add(this.line);
  }

  clear() {
    this.points = [];
    this.#activePoints = 0;
    this.line.visible = false;
  }

  /**
   * Feed the viewer position for the near fade (viewerFade: 'uniform'). In XR
   * pass the eye midpoint, not a per-eye camera position, so both eyes fade
   * the trail at the same place.
   */
  setViewerPosition(position: THREE.Vector3) {
    this.uViewer.value.copy(position);
  }

  addPoint() {
    const p = this.golfBall.position;
    const last = this.points[this.points.length - 1];
    if (!last || last.distanceToSquared(p) > 1e-6) {
      this.points.push(p.clone());
    }
  }

  update(collectPoints = false) {
    // let dirty = false;

    const frameMod = this.sampleEvery;
    // if (collectPoints && this.frameNum % 4 === 0 && this.points.length < this.maxPoints) {
    if (collectPoints && this.frameNum % frameMod === 0 && this.points.length < this.maxPoints) {
      this.addPoint();
      // dirty = true;
    }

    // if (collectPoints && this.frameNum % 2 === 0) {
    //   dirty = true;
    // }

    this.frameNum++;

    // if (dirty) {
    //   this._rebuild();
    // }
    if (collectPoints && this.frameNum % 2 === 0) {
      this._updatePositions();
    }

  }

  _rebuild() {
    const live = this.golfBall.position;
    const last = this.points[this.points.length - 1];
    const raw = (!last || last.distanceToSquared(live) > 1e-6)
      ? [...this.points, live.clone()]
      : this.points;

    if (raw.length < 2) {
      this.line.visible = false;
      return;
    }

    const head = resampleByArcLength(raw, this.resampleSpacing);
    if (head.length < 2) {
      this.line.visible = false;
      return;
    }

    // Compute total arc length for fade fraction
    let total = 0;
    for (let i = 1; i < head.length; i++) {
      total += head[i].distanceTo(head[i - 1]);
    }

    // Update the fade fraction: fadeLength / totalLength
    // Capped at 0.49 so both ends don't overlap
    this.fadeFracUniform.value = Math.min(0.49, this.fadeLength / Math.max(total, 0.0001));

    // Build flat positions array
    const positions = new Float32Array(head.length * 3);
    for (let i = 0; i < head.length; i++) {
      positions[i * 3]     = head[i].x;
      positions[i * 3 + 1] = head[i].y;
      positions[i * 3 + 2] = head[i].z;
    }

    // Update the line geometry and rebuild
    this.line.lines(positions).build();
    this.line.visible = true;
    this.line.frustumCulled = false;
    this.line.layers.set(this.layer);
    this.line.renderOrder = this.renderOrder;
  }

  dispose() {
    // @ts-expect-error makio-meshline raycast signature doesn't match Object3D
    this.scene.remove(this.line);
    this.line.geometry.dispose();
    // this.line.material.dispose();
    const mat = this.line.material;
    if (Array.isArray(mat)) {
      mat.forEach(m => m.dispose());
    } else {
      mat.dispose();
    }

  }

  reset(updatedTarget?: THREE.Object3D) {
    if (updatedTarget) {
      this.golfBall = updatedTarget;
    }
    this.line.visible = false;
    this.points = [];
    this.frameNum = 0;
    this.#activePoints = 0;
    this.#built = false;
  }

  _updatePositions() {
    const live = this.golfBall.position;
    const last = this.points[this.points.length - 1];
    const raw = (!last || last.distanceToSquared(live) > 1e-6)
      ? [...this.points, live.clone()]
      : this.points;

    if (raw.length < 2) {
      this.line.visible = false;
      return;
    }

    const head = resampleByArcLength(raw, this.resampleSpacing);
    if (head.length < 2) {
      this.line.visible = false;
      return;
    }

    let total = 0;
    for (let i = 1; i < head.length; i++) {
      total += head[i].distanceTo(head[i - 1]);
    }
    this.fadeFracUniform.value = Math.min(0.49, this.fadeLength / Math.max(total, 0.0001));

    this.#activePoints = Math.min(head.length, this.maxPoints);
    // Must be derived from the CURRENT point count. The opacity hook divides
    // progress by this ratio, so the old stale read (assigned before
    // #activePoints, hence 0 on the first update) made the divide blow up:
    // remapped clamped to 1, fadeOut smoothstepped to 0, and the whole line
    // drew at alpha 0. Long shots eventually accumulated enough points for the
    // one-update lag to stop mattering, which is why only short shots looked
    // like the tracer was missing entirely.
    this.activeRatioUniform.value = this.#activePoints / this.maxPoints;
    for (let i = 0; i < this.#activePoints; i++) {
      this.#positions[i * 3]     = head[i].x;
      this.#positions[i * 3 + 1] = head[i].y;
      this.#positions[i * 3 + 2] = head[i].z;
    }

    // Park every unused tail vertex ON the trail's current end point — every
    // update, not once. Filling only on the first update left the tail frozen
    // at wherever the ball was back then (the tee), and the ribbon then drew
    // one long, half-faded segment from the live end of the tracer straight
    // back to the start point. Collapsed onto the end point, the tail segments
    // have zero length and can't render at all.
    const lastPt = head[this.#activePoints - 1];
    for (let i = this.#activePoints; i < this.maxPoints; i++) {
      this.#positions[i * 3]     = lastPt.x;
      this.#positions[i * 3 + 1] = lastPt.y;
      this.#positions[i * 3 + 2] = lastPt.z;
    }


    if (!this.#built) {
      this.line.lines(this.#positions).build();
      this.#built = true;
    } else {
      // this.line.geometry.setPositions(this.#positions, true);
      (this.line.geometry as MeshLineGeometry).setPositions(this.#positions, true);
    }

    this.line.visible = true;
    this.line.frustumCulled = false;
    this.line.layers.set(this.layer);
    this.line.renderOrder = this.renderOrder;
  }
}